import _, {find} from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {CalendarEventModel} from '../../../models/api/calendarEventModel';
import {CalendarEventVersionModel} from '../../../models/api/calendarEventVersionModel';
import {CalendarModel} from '../../../models/api/calendarModel';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {getComposeCalendarVersion} from '../../data/calendarVersions/calendarVersionsSelectors';
import {getChannels} from '../../data/channels/channelsSelectors';
import {
  getCalendarEvents,
  getCalendarEventVersions,
  getCalendars,
  getCalendarVersions,
} from '../../data/dataSelectors';
import {getSelectedSideFrameId} from '../../data/localPreferences/localPreferencesSelectors';
import {getFlatPanels, getPanel} from '../panelsSelectors';
import {isCalendarsSidePanelId, PanelTypesEnum} from '../panelStateBase';
import {
  CalendarEventGridParameters,
  CalendarEventOverrides,
  FocusedEventSourcesEnum,
} from './calendarsPanelState';

/*
 * Calendars panel.
 */

export const getCalendarsPanels = createSelector(getFlatPanels, (panels) =>
  _(panels)
    .map((p) => p.type === PanelTypesEnum.CALENDARS && p)
    .compact()
    .value(),
);

export function makeGetCalendarsPanel() {
  return createSelector(getPanel, (panel) => {
    // A corresponding panel should exist, otherwise we have a problem.
    if (!panel || panel.type !== PanelTypesEnum.CALENDARS) {
      throw new Error('This calendar panel could not be found.');
    }

    return panel;
  });
}

// TEMPORARY: This only supports a single side-panel.
export const getCalendarSidePanel = createSelector(getCalendarsPanels, (panels) =>
  find(panels, (panel) => isCalendarsSidePanelId(panel.id)),
);

export function makeGetCalendarsParameters(getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.parameters,
  );
}

export function makeGetCalendarsEventVersionUids(
  getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>,
) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.calendarEventVersionUids,
  );
}

export function makeGetHighlightedCalendarEventParameters(
  getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>,
) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.highlightedCalendarEventParameters,
  );
}

export function makeGetFocusedCalendarEventParameters(
  getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>,
) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.focusedCalendarEventParameters,
  );
}

export function makeGetPopoverCalendarEventParameters(
  getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>,
) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.popoverCalendarEventParameters,
  );
}

export function makeGetViewingNowCalendars(getCalendarsPanel?: ReturnType<typeof makeGetCalendarsPanel>) {
  return createSelector(
    getCalendarsPanel || makeGetCalendarsPanel(),
    (calendarsPanel) => calendarsPanel.viewingNowCalendars,
  );
}

export function makeGetSelectedCalendarVersionIds(
  getCalendarsParameters?: ReturnType<typeof makeGetCalendarsParameters>,
) {
  return createSelector(
    getCalendarsParameters || makeGetCalendarsParameters(),
    (parameters) => parameters.selectedCalendarVersionIds,
  );
}

export const getIsSidePanelCalendarVisible = createSelector(
  getSelectedSideFrameId,
  (sideFrameId) => sideFrameId === FrameTypesEnum.CALENDAR,
);

/*
 * Calendar event data.
 */

// The full group of calendar data necessary to display event viewer/editor components.
export interface CalendarEventData {
  calendarEventVersion: CalendarEventVersionModel;
  calendarEvent: CalendarEventModel;
  calendarVersion: CalendarVersionModel;
  calendar: CalendarModel;
  source: FocusedEventSourcesEnum;
  overrides: CalendarEventOverrides;
  instanceStartDate?: number;
  channel?: ChannelModel;
}

export function makeGetCalendarEventData(
  getCalendarEventParameters: AppParametricSelector<any, CalendarEventGridParameters | undefined>,
) {
  return createSelector(
    [
      getCalendarEventParameters,
      getCalendarEventVersions,
      getCalendarEvents,
      getCalendarVersions,
      getCalendars,
      getChannels,
    ],
    (
      calendarEventParameters,
      calendarEventVersions,
      calendarEvents,
      calendarVersions,
      calendars,
      channels,
    ): CalendarEventData | undefined => {
      if (!calendarEventParameters) {
        return undefined;
      }

      const {calendarEventVersionUid, calendarVersionId} = calendarEventParameters;
      const calendarEventVersion = calendarEventVersions[calendarEventVersionUid];
      if (!calendarEventVersion) {
        return undefined;
      }

      const calendarVersion = calendarVersions[calendarVersionId];
      if (!calendarVersion) {
        return undefined;
      }

      const calendarEvent = calendarEvents[calendarEventVersion.calendarEventUid];
      const calendar = calendars[calendarVersion.calendarId];
      if (!calendarEvent || !calendar) {
        return undefined;
      }

      const channel = calendarVersion.channelId ? channels[calendarVersion.channelId] : undefined;
      if (!channel && calendarVersion.channelId) {
        return undefined;
      }

      return {
        calendarEventVersion,
        calendarEvent,
        calendarVersion,
        calendar,
        channel,
        instanceStartDate: calendarEventParameters.instanceStartDate,
        source: calendarEventParameters.source,
        overrides: calendarEventParameters.overrides,
      };
    },
  );
}

export function buildCalendarEventParametersFromData(
  calendarEventData: CalendarEventData,
): CalendarEventGridParameters {
  return {
    calendarEventVersionUid: calendarEventData.calendarEventVersion.uid,
    calendarVersionId: calendarEventData.calendarVersion.id,
    overrides: calendarEventData.overrides,
    source: calendarEventData.source,
    instanceStartDate: calendarEventData.instanceStartDate,
  };
}

export function makeGetCanCreateNewEvent() {
  const getFocusedCalendarEventParameters = makeGetFocusedCalendarEventParameters();
  const getFocusedCalendarEventData = makeGetCalendarEventData(getFocusedCalendarEventParameters);

  return createSelector(
    [getFocusedCalendarEventData, getComposeCalendarVersion],
    (focusedCalendarEventData, composeCalendarVersion) =>
      Boolean(!focusedCalendarEventData && composeCalendarVersion),
  );
}

type GetCalendarVersionId = AppParametricSelector<any, number>;
export function makeGetCalendar(getCalendarVersionId: GetCalendarVersionId) {
  return createSelector(
    [getCalendarVersionId, getCalendarVersions, getCalendars],
    (calendarVersionId, calendarVersions, calendars) => {
      const calendarVersion = calendarVersions[calendarVersionId];
      if (!calendarVersion) {
        return undefined;
      }

      return calendars[calendarVersion.calendarId];
    },
  );
}

export function makeGetCalendarOrganizerAddress(getCalendarVersionId: GetCalendarVersionId) {
  const getCalendar = makeGetCalendar(getCalendarVersionId);
  return createSelector(getCalendar, (calendar) => calendar?.organizerAddress);
}

export function makeGetSelectedCalendarsData(
  getSelectedCalendarVersionIds: ReturnType<typeof makeGetSelectedCalendarVersionIds>,
) {
  return createSelector(
    [getSelectedCalendarVersionIds, getCalendarVersions, getCalendars],
    (selectedCalendarVersionIds, calendarVersions, calendars) =>
      selectedCalendarVersionIds.map((calendarVersionId) => {
        const calendarVersion = calendarVersions[calendarVersionId];
        if (!calendarVersion) {
          throw new Error('Unable to find calendar version');
        }

        const calendar = calendars[calendarVersion.calendarId];
        if (!calendar) {
          throw new Error('Unable to find calendar');
        }

        return {calendar, calendarVersion};
      }),
  );
}
